import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { InstallBanner } from "./components/InstallBanner";
import { ChatFab } from "./components/ChatFab";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { TutorialProvider } from "./contexts/TutorialContext";
import { TutorialOverlay } from "./components/tutorial/TutorialOverlay";
import { ShieldOff } from "lucide-react";

// Pages
import { Login } from "./pages/Login";
import { Register } from "./pages/Register";
import { ForgotPassword } from "./pages/ForgotPassword";
import { ResetPassword } from "./pages/ResetPassword";
import { Dashboard } from "./pages/Dashboard";
import { Clienti } from "./pages/Clienti";
import { Preventivi } from "./pages/Preventivi";
import { Fatturazione } from "./pages/Fatturazione";
import { Scadenziario } from "./pages/Scadenziario";
import { Progetti } from "./pages/Progetti";
import { Servizi } from "./pages/Servizi";
import { Report } from "./pages/Report";
import { Impostazioni } from "./pages/Impostazioni";
import { Chat } from "./pages/Chat";
import { Calendario } from "./pages/Calendario";
import { Domini } from "./pages/Domini";
import { Contratti } from "./pages/Contratti";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="spinner" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

const AccessDenied = () => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center">
        <ShieldOff className="h-12 w-12 text-slate-300 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-slate-700 mb-2">Accesso negato</h2>
        <p className="text-slate-500 mb-6">Non hai i permessi per visualizzare questa sezione.</p>
        <button
          onClick={() => navigate('/')}
          className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm font-medium"
        >
          Torna alla Dashboard
        </button>
      </div>
    </div>
  );
};

// Permission Route Component
const PermissionRoute = ({ children, section }) => {
  const { user, loading, hasPermission } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="spinner" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (!hasPermission(section)) return <AccessDenied />;
  return children;
};

// Public Route Component (redirect if already logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="spinner" />
      </div>
    );
  }

  if (user) {
    return <Navigate to="/" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <Register />
          </PublicRoute>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <PublicRoute>
            <ForgotPassword />
          </PublicRoute>
        }
      />
      <Route path="/reset-password" element={<ResetPassword />} />

      {/* Protected Routes */}
      <Route path="/" element={<PermissionRoute section="dashboard"><Dashboard /></PermissionRoute>} />
      <Route path="/clienti" element={<PermissionRoute section="clienti"><Clienti /></PermissionRoute>} />
      <Route path="/preventivi" element={<PermissionRoute section="preventivi"><Preventivi /></PermissionRoute>} />
      <Route path="/fatturazione" element={<PermissionRoute section="fatturazione"><Fatturazione /></PermissionRoute>} />
      <Route path="/scadenziario" element={<PermissionRoute section="scadenziario"><Scadenziario /></PermissionRoute>} />
      <Route path="/progetti" element={<PermissionRoute section="progetti"><Progetti /></PermissionRoute>} />
      <Route path="/domini" element={<PermissionRoute section="domini"><Domini /></PermissionRoute>} />
      <Route path="/contratti" element={<PermissionRoute section="contratti"><Contratti /></PermissionRoute>} />
      <Route path="/chat" element={<PermissionRoute section="chat"><Chat /></PermissionRoute>} />
      <Route path="/calendario" element={<PermissionRoute section="calendario"><Calendario /></PermissionRoute>} />
      <Route path="/servizi" element={<PermissionRoute section="servizi"><Servizi /></PermissionRoute>} />
      <Route path="/report" element={<PermissionRoute section="report"><Report /></PermissionRoute>} />
      <Route path="/impostazioni" element={<PermissionRoute section="impostazioni"><Impostazioni /></PermissionRoute>} />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <TutorialProvider>
          <AppRoutes />
          <TutorialOverlay />
          <Toaster position="top-right" richColors closeButton />
          <InstallBanner />
          <ChatFab />
        </TutorialProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
